import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, LayoutTemplate } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { DEMOS } from "../config/site";
import Mockup from "./Mockup";

function DemoModal({ demo, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md sm:p-6"
      onClick={onClose}
      data-testid="demo-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 20 }}
        transition={{ duration: 0.45, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl p-5 sm:p-7"
        data-testid="demo-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close preview"
          data-testid="demo-modal-close"
          className="glass absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full text-slate-300 transition-colors hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="h-64 sm:h-80">
          <Mockup title={demo.title} url={demo.url} variant={demo.variant} large />
        </div>

        <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-indigo-300">
              {demo.title}
            </p>
            <h3 className="mt-1.5 font-display text-2xl font-bold text-white">
              {demo.title} — Live Preview
            </h3>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-400">
              {demo.desc}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <a
            href={demo.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="demo-modal-open-live"
            className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
          >
            Open Live Site <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href="#contact"
            onClick={onClose}
            data-testid="demo-modal-request-similar"
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
          >
            Request a Similar Website
          </a>
        </div>

        <p className="mt-4 flex items-center gap-2 font-mono text-[10px] text-slate-500">
          <LayoutTemplate className="h-3 w-3" />
          Preview link: <span className="text-slate-400">{demo.url}</span>
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function Demos() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="demos"
      className="relative scroll-mt-24 py-24 sm:py-28"
      data-testid="demos-section"
    >
      <div
        className="pointer-events-none absolute left-0 top-1/4 h-[420px] w-[420px] rounded-full bg-indigo-600/[0.08] blur-[130px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="My Work"
          title={
            <>
              Demo <span className="text-gradient">Websites</span>
            </>
          }
          sub="A showcase of website demos I build with AI — each category can be tailored and published for your business."
        />

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DEMOS.map((d, i) => (
            <Reveal key={d.title} delay={0.06 * (i % 3)} className="h-full">
              <article
                className="glass card-hover group flex h-full flex-col overflow-hidden rounded-2xl p-4"
                data-testid={`demo-card-${i + 1}`}
              >
                <div className="h-48 transition-transform duration-500 group-hover:scale-[1.02]">
                  <Mockup title={d.title} url={d.url} variant={d.variant} />
                </div>
                <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-4">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-indigo-300/90">
                    {d.title}
                  </p>
                  <h3 className="mt-2 font-display text-xl font-bold text-white">
                    {d.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">
                    {d.desc}
                  </p>
                  <button
                    onClick={() => setActive(d)}
                    data-testid={`demo-view-button-${i + 1}`}
                    className="btn-ghost mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
                  >
                    View Demo
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <DemoModal demo={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
